"use client";

import useTranslate from "@app/_hooks/useTranslate";
import { Button } from "@ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@ui/dialog";
import { XIcon } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { useState } from "react";
import ForgottenPasswordForm from "./ForgottenPasswordForm";
import SignInForm from "./SignInForm";

type LoginButtonProps = {
  className?: string;
  children: ReactNode;
  redirectOnSuccess?: boolean;
  onSuccess?: () => void;
  variant?: ComponentProps<typeof Button>["variant"];
  size?: ComponentProps<typeof Button>["size"];
};

export function LoginButton({
  className,
  children,
  redirectOnSuccess = true,
  onSuccess,
  variant = "link",
  size,
}: LoginButtonProps) {
  const t = useTranslate();
  const [open, setOpen] = useState(false);
  const [resetPasswordDialog, setResetPasswordDialog] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={variant} size={size} className={className}>
          {children}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md p-6">
        <DialogClose className="absolute top-0 right-0 p-4">
          <XIcon className="size-4" />
          <span className="sr-only">{t("close")}</span>
        </DialogClose>
        <SignInForm
          redirectOnSuccess={redirectOnSuccess}
          callback={() => {
            setOpen(false);
            onSuccess?.();
          }}
          header={DialogTitle}
          description={DialogDescription}
        >
          <Dialog
            open={resetPasswordDialog}
            onOpenChange={setResetPasswordDialog}
          >
            <Button
              type="button"
              variant="link"
              className="w-full"
              onClick={() => setResetPasswordDialog(true)}
            >
              {t("forgotPassword")}
            </Button>
            <DialogContent className="p-8">
              <DialogClose className="absolute top-0 right-0 p-4">
                <XIcon className="size-4" />
                <span className="sr-only">{t("close")}</span>
              </DialogClose>
              <ForgottenPasswordForm
                callback={() => setResetPasswordDialog(false)}
                header={DialogTitle}
                description={DialogDescription}
              />
            </DialogContent>
          </Dialog>
        </SignInForm>
      </DialogContent>
    </Dialog>
  );
}

export default LoginButton;
